// src/views/folders/hooks/useTreeOperations.ts
/* eslint-disable max-len */
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NodeApi } from 'react-arborist';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { moveItem } from '../../folders/slice/actions';
import { getActiveList } from '../../auth/slice/selectors';
import { setActiveList } from '../../auth/slice/actions';
import { getListOfWordsList } from '../slice/selectors';
import { fetchWordsListPage } from '../slice/actions';
import { TreeNodeType } from '../../../services/ApiService';

const MAX_NESTING_LEVEL = 5;

// Cache globalne - zostają bez zmian jako WeakMap
let nodeCache = new WeakMap<TreeNodeType[], Map<string, TreeNodeType | null>>();
let pathCache = new WeakMap<TreeNodeType[], Map<string, string[] | null>>();

export const useTreeOperations = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const activeList = useSelector(getActiveList);
  const wordsLists = useSelector(getListOfWordsList);

  // POPRAWKA 1: Poprawiona funkcja isDescendantOf
  const isDescendantOf = useCallback(
    (possibleParent: NodeApi<TreeNodeType>, possibleChild: NodeApi<TreeNodeType>): boolean => {
      let current = possibleChild.parent; // POPRAWKA: Zaczynamy od rodzica dziecka
      while (current) {
        if (current.id === possibleParent.id) return true; // POPRAWKA: Sprawdzamy czy to możliwy rodzic
        current = current.parent;
      }

      return false;
    },
    [],
  );

  // Funkcja znajdowania węzła - bez zmian, rekurencja jest OK
  const findNodeById = useCallback((nodes: TreeNodeType[], id: string): TreeNodeType | null => {
    let nodesMap = nodeCache.get(nodes);
    if (!nodesMap) {
      nodesMap = new Map();
      nodeCache.set(nodes, nodesMap);
    }

    if (nodesMap.has(id)) {
      return nodesMap.get(id)!;
    }

    const searchNode = (nodeList: TreeNodeType[]): TreeNodeType | null => {
      const found = nodeList.find((node) => node.id === id);
      if (found) {
        nodesMap!.set(id, found);

        return found;
      }

      return nodeList.reduce<TreeNodeType | null>((result, node) => {
        if (result) return result;
        if (node.children && node.children.length > 0) {
          return searchNode(node.children);
        }

        return null;
      }, null);
    };

    const result = searchNode(nodes);
    nodesMap.set(id, result);

    return result;
  }, []);

  // Znajdowanie ścieżki - bez zmian
  const findPathToNode = useCallback((nodes: TreeNodeType[], targetId: string): string[] | null => {
    let pathsMap = pathCache.get(nodes);
    if (!pathsMap) {
      pathsMap = new Map();
      pathCache.set(nodes, pathsMap);
    }

    if (pathsMap.has(targetId)) {
      return pathsMap.get(targetId)!;
    }

    // eslint-disable-next-line max-len
    const findPath = (nodeList: TreeNodeType[], currentPath: string[] = []): string[] | null => nodeList.reduce<string[] | null>((result, node) => {
      if (result) return result;

      if (node.id === targetId) {
        const fullPath = [...currentPath, node.id];
        pathsMap!.set(targetId, fullPath);

        return fullPath;
      }

      if (node.children && node.children.length > 0 && node.type === 'folder') {
        return findPath(node.children, [...currentPath, node.id]);
      }

      return null;
    }, null);

    const result = findPath(nodes);
    pathsMap.set(targetId, result);

    return result;
  }, []);

  // Obliczanie poziomu węzła - bez zmian
  const calculateNodeLevel = useCallback((node: NodeApi<TreeNodeType>): number => {
    const getLevel = (currentNode: NodeApi<TreeNodeType> | null, level: number = 0): number => {
      if (!currentNode?.parent || level >= MAX_NESTING_LEVEL) {
        return level;
      }

      return getLevel(currentNode.parent, level + 1);
    };

    return getLevel(node);
  }, []);

  // POPRAWKA 2: Ulepszona obsługa drop z await
  const handleDrop = useCallback(async (args: any) => {
    try {
      const { parentNode, dragNodes, index } = args;
      if (!dragNodes?.length) return;

      const dragNode = dragNodes[0];
      const { type, id } = dragNode.data;
      const itemId = parseInt(id.replace(`${type}-`, ''), 10);

      // Walidacja dla list
      if (parentNode?.data?.type === 'list') {
        message.warning(t('folders:messages.cannotMoveIntoList'));

        return;
      }

      // POPRAWKA: Sprawdzenie czy nie przeciągamy do samego siebie
      if (dragNode.id === parentNode?.id) {
        message.warning(t('folders:messages.cannotMoveIntoItself'));

        return;
      }

      // Sprawdź cykliczne zależności dla folderów - używamy poprawionej funkcji
      if (type === 'folder' && parentNode) {
        if (isDescendantOf(parentNode, dragNode)) {
          message.warning(t('folders:messages.cannotMoveIntoSubfolder'));

          return;
        }
      }

      // Walidacja poziomów zagnieżdżenia
      if (parentNode?.data?.type === 'folder' && type === 'folder') {
        const parentLevel = calculateNodeLevel(parentNode);
        if (parentLevel >= MAX_NESTING_LEVEL - 1) {
          message.warning(t('folders:messages.maxNesting', { max: MAX_NESTING_LEVEL }));

          return;
        }
      }

      const targetFolderId = parentNode?.data?.type === 'folder'
        ? parseInt(parentNode.data.id.replace('folder-', ''), 10)
        : null;

      // POPRAWKA: Czekamy na zakończenie operacji
      await dispatch(moveItem(itemId, type, targetFolderId, index));
      // Komunikat sukcesu powinien być w akcji Redux
    } catch (error) {
      console.error('Drop error:', error);
      message.error(t('folders:messages.moveError'));
    }
  }, [dispatch, calculateNodeLevel, isDescendantOf, t]);

  // POPRAWKA 3: Ulepszona obsługa aktywacji listy
  const handleToggleActive = useCallback(async (node: NodeApi<TreeNodeType>) => {
    if (node.data.type !== 'list') return;

    const listId = parseInt(node.data.id.replace('list-', ''), 10);
    const wordsCount = node.data.data?.wordsCount ?? 0;

    if (wordsCount < 2) {
      message.warning(t('folders:messages.listTooShort'));

      return;
    }

    // Dezaktywacja obecnej listy
    if (activeList?.id === listId) {
      dispatch(setActiveList(null));
      // message.info('Lista dezaktywowana');

      return;
    }

    // Znajdź listę w store
    let listWithFullData = wordsLists.find((list: any) => list.id === listId);

    if (!listWithFullData || !listWithFullData.words || listWithFullData.words.length === 0) {
      const loadingMessage = message.loading(t('games:states.loadingList'), 0);

      try {
        // Próbujemy załadować dane
        await dispatch(fetchWordsListPage(1) as any);

        // Czekamy chwilę na aktualizację store przez Redux
        await new Promise((resolve) => setTimeout(resolve, 500));

        // To nie jest idealne, ale zakładamy że po dispatch
        // komponent otrzyma nowe props przez useSelector
        // i przy następnym wywołaniu wordsLists będzie zaktualizowane
      } catch (error) {
        console.error('Error loading list:', error);
        message.error(t('folders:messages.listLoadError'));

        return;
      } finally {
        loadingMessage();
      }

      // Po załadowaniu próbujemy znaleźć listę ponownie
      // To zadziała tylko jeśli komponent się przerenderował
      listWithFullData = wordsLists.find((list: any) => list.id === listId);
    }

    // Aktywuj listę
    if (listWithFullData && listWithFullData.words && listWithFullData.words.length >= 2) {
      dispatch(setActiveList(listWithFullData));
      // message.success('Lista aktywowana');
    } else {
      // Fallback - aktywuj z podstawowymi danymi i zaznacz że wymaga doładowania
      const basicListData = {
        id: listId,
        name: node.data.name,
        words: [],
        needsLoading: true,
      };
      dispatch(setActiveList(basicListData));
      message.info(t('folders:messages.listActivated'));

      // Próbuj załadować dane w tle
      dispatch(fetchWordsListPage(1) as any).catch((error: any) => {
        console.error('Background loading failed:', error);
      });
    }
  }, [activeList, dispatch, wordsLists, t]);

  // POPRAWKA 4: Właściwe czyszczenie cache
  const clearCache = useCallback(() => {
    // Tworzymy nowe instancje WeakMap
    // Stare zostaną automatycznie usunięte przez GC
    nodeCache = new WeakMap();
    pathCache = new WeakMap();
  }, []);

  // Cleanup effect - bez zmian
  useEffect(() => () => {
    // WeakMap automatycznie usuwa nieużywane klucze
    // Możemy wymusić GC jeśli jest dostępny
    if (typeof global !== 'undefined' && global.gc) {
      global.gc();
    }
  }, []);

  return {
    findNodeById,
    findPathToNode,
    calculateNodeLevel,
    handleDrop,
    handleToggleActive,
    clearCache,
    MAX_NESTING_LEVEL,
  };
};
